import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  JourneyDraftEditForm,
  JourneyDraftEditInitialValues,
  JourneyDraftComponents,
  JourneyDraftAdditionalParams,
  JourneyDraftFormOptions,
  onJourneyDraftFormSaved,
  IJourneyDraftFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedJourneyDraft, ISharedPatchJourneyDraft, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchJourneyDraft, patchJourneyDraft, getJourneyDraftUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useJourneies, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedJourneyDraft> {}

const JourneyDraftFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const journeies = useJourneies();

  useEffect(() => {
    fetchJourneyDraft(id).then(journey_draft => setValue(journey_draft));
  }, [id, setValue, ]);

  const param_props: IJourneyDraftFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    journeies: journeies.journeies,
  };
  const formLayout: IForm | undefined = useMemo(() => value && JourneyDraftEditForm(value, param_props, t), [value, organizations, journeies, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && JourneyDraftFormOptions(value, param_props, t), [value, organizations, journeies, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchJourneyDraft, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchJourneyDraft) => value && save(() => patchJourneyDraft(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onJourneyDraftFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !journeies.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.Journey, value)}
    >
      <AtlasForm<ISharedPatchJourneyDraft>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={JourneyDraftComponents}
        additionalParams={JourneyDraftAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={JourneyDraftEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('JOURNEY_DRAFT')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const JourneyDraftFormEditView = React.memo(withEdit(JourneyDraftFormEdit, getJourneyDraftUpdateSchema));
